import classNames from 'classnames';
import React, { ReactNode } from 'react';
import ILazyScrollBaseProps from './LazyScrollBaseProps';

class LazyScrollBase<T> extends React.Component<ILazyScrollBaseProps<T>> {
  public state = {
    rows: [],
  };
  private rendering = false;
  private numRows = 0;
  private finished = false;
  private scroller: HTMLElement | Document | null = null;

  public componentDidMount() {
    this.updateTrigger();

    if (this.shouldLoadMore()) {
      this.loadMore();
    }
  }

  public updateTrigger() {
    const trigger = this.getTrigger();
    if (trigger) {
      trigger.addEventListener('scroll', this.onScroll);
    }
  }

  public componentDidUpdate(prevProps: ILazyScrollBaseProps<T>, prevState: { rows: T[] }) {
    this.rendering = false;
    if (prevState.rows.length !== this.state.rows.length) {
      if (this.shouldLoadMore()) {
        this.loadMore();
      }
    }

    if (!this.finished && this.state.rows.length === this.props.data.length) {
      this.finished = true;
      if (this.props.onDidLoad) {
        this.props.onDidLoad();
      }
    }

    if (prevProps.data !== this.props.data) {
      this.finished = false;
      const dataLength = this.props.data.length;
      if (this.numRows > dataLength) {
        this.numRows = dataLength;
      }
      this.setState({ rows: this.props.data.slice(0, this.numRows) });
      if (this.shouldLoadMore()) {
        this.loadMore();
      }
    }

    if (prevProps.trigger !== this.props.trigger) {
      this.updateTrigger();
    }
  }

  public componentWillUnmount() {
    const trigger = this.getTrigger();
    if (trigger) {
      trigger.removeEventListener('scroll', this.onScroll);
    }
  }

  public render() {
    const { tag, height, className, forwardRef, template, renderRow, style, trigger } = this.props;

    const classes = classNames(
      {
        'lazy-scroll': true,
      },
      className
    );

    const render = template ? template : (rs: ReactNode | ReactNode[]) => rs;
    const rows = this.state.rows.map((item, index) => {
      return renderRow(item, index);
    });

    return React.createElement(
      tag,
      {
        ref: (el: HTMLElement) => {
          if (forwardRef) {
            forwardRef(el);
          }
          if (trigger === 'self') {
            this.scroller = el;
          } else if (trigger !== 'window') {
            this.scroller = trigger;
          }
        },
        style: {
          maxHeight: height,
          height,
          ...style,
        },
        className: classes,
      },
      render(rows)
    );
  }

  private onScroll = () => {
    if (this.shouldLoadMore()) {
      this.loadMore();
    }
  };

  private shouldLoadMore() {
    return !this.rendering && this.props.data.length > this.state.rows.length && this.props.shouldLoadMore();
  }

  private getTrigger() {
    return this.props.trigger === 'window' ? document : this.scroller;
  }

  private loadMore() {
    this.rendering = true;
    const totalRows = this.props.data.length;
    this.numRows = this.numRows + this.props.sectionSize;

    if (this.numRows >= totalRows) {
      this.numRows = totalRows;
    }
    const rows = this.props.data.slice(0, this.numRows);
    this.setState({ rows });
  }
}

export default LazyScrollBase;
